import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Form, Grid, Input, InputNumber, Radio } from '@arco-design/web-react';

import { BindingProviderStep } from '@/components-biz/ModelSubscriptionForm';
import { useLangContext } from '@/hooks';

import { isFieldVisible, resolveFieldMode } from '../../platform/capability-registry/resolveFieldMode';
import type { CapabilityControlProps } from '../../platform/capability-registry/types';
import { formatQuantityUnit } from '../../utils/formatQuantityUnit';
import { MsPricingProviderSelector } from './MsPricingProviderSelector';
import { MS_PROVIDER_TOKEN_RESOURCE_CODE, MS_PROVIDER_TOKEN_UNIT } from './constants';
import type { DirectTokenQuotaMode } from './directTokenQuotaMode';
import { resolveDirectTokenQuotaMode } from './directTokenQuotaMode';
import { MS_PROVIDER_FIELD_KEYS } from './fieldKeys';
import type { ModelSubscriptionProviderBlockProps, ModelSubscriptionProviderPaths } from './types';
import { deriveMsUseSubResourcePricing } from './useSubResourcePricingField';

const MAX_SAFE_QUOTA = Number.MAX_SAFE_INTEGER;

const numberFormatter = (value: number | string | undefined) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const numberParser = (value: string | undefined) => String(value || '').replace(/,/g, '');

function isFieldLocked(control: CapabilityControlProps, fieldKey: string) {
  const mode = resolveFieldMode(fieldKey, control);
  return mode === 'readonly' || mode === 'disabled';
}

interface QuotaInputProps {
  field: string;
  label: string;
  rules: { required?: boolean; message?: string }[];
  required: boolean;
  disabled: boolean;
  isPreview: boolean;
  /** 计价模式展示数量单位（直建不展示） */
  unitLabel?: string;
}

function QuotaInput({ field, label, rules, required, disabled, isPreview, unitLabel }: QuotaInputProps) {
  const { t } = useTranslation();

  return (
    <Form.Item
      field={field}
      label={label}
      rules={rules}
      required={required}
      validateTrigger={required ? ['onChange', 'onBlur', 'onSubmit'] : ['onBlur']}
    >
      <InputNumber
        min={1}
        max={MAX_SAFE_QUOTA}
        precision={0}
        step={1000}
        style={{ width: '100%' }}
        disabled={disabled || isPreview}
        placeholder={t('common:input.placeholder.name', { name: label })}
        formatter={numberFormatter}
        parser={numberParser}
        suffix={unitLabel || undefined}
      />
    </Form.Item>
  );
}

interface TokenQuotaFieldsProps {
  form: ModelSubscriptionProviderBlockProps['form'];
  paths: ModelSubscriptionProviderPaths;
  /** 是否展示 Token 总量（与输入/输出互斥） */
  showBillingTotalTokens: boolean;
  showInputTokens: boolean;
  showOutputTokens: boolean;
  billingTotalTokensReadOnly: boolean;
  inputTokensReadOnly: boolean;
  outputTokensReadOnly: boolean;
  isPreview: boolean;
  /** 当前展示的配额字段是否必填 */
  required?: boolean;
  /** 直建：展示总量 / 输入输出二选一切换 */
  allowModeSwitch?: boolean;
  /** 计价模式：输入框展示单位 */
  showUnit?: boolean;
}

/** Token 配额：billingTotalTokens 与 input/output 互斥 */
function TokenQuotaFields({
  form,
  paths,
  showBillingTotalTokens,
  showInputTokens,
  showOutputTokens,
  billingTotalTokensReadOnly,
  inputTokensReadOnly,
  outputTokensReadOnly,
  isPreview,
  required = false,
  allowModeSwitch = false,
  showUnit = false,
}: TokenQuotaFieldsProps) {
  const { t } = useTranslation();
  const { lang } = useLangContext();
  const unitLabel = showUnit ? formatQuantityUnit(MS_PROVIDER_TOKEN_UNIT, lang) : undefined;
  const billingTotalWatch = Form.useWatch(paths.billingTotalTokens, form);
  const inputTokensWatch = Form.useWatch(paths.inputTokens, form);
  const outputTokensWatch = Form.useWatch(paths.outputTokens, form);

  const [directMode, setDirectMode] = useState<DirectTokenQuotaMode>(
    () =>
      resolveDirectTokenQuotaMode({
        billingTotalTokens: form.getFieldValue(paths.billingTotalTokens),
        inputTokens: form.getFieldValue(paths.inputTokens),
        outputTokens: form.getFieldValue(paths.outputTokens),
      }) ?? 'total',
  );

  // 编辑异步回填后按配额倒推展示方式；无有效值时不覆盖用户手动切换
  useEffect(() => {
    if (!allowModeSwitch) return;
    const inferred = resolveDirectTokenQuotaMode({
      billingTotalTokens: billingTotalWatch,
      inputTokens: inputTokensWatch,
      outputTokens: outputTokensWatch,
    });
    if (inferred) {
      setDirectMode(inferred);
    }
  }, [allowModeSwitch, billingTotalWatch, inputTokensWatch, outputTokensWatch]);

  const requiredRules = useMemo(
    () =>
      required
        ? [
            {
              required: true,
              message: t('common:required.field'),
            },
          ]
        : [],
    [required, t],
  );

  const effectiveShowTotal = allowModeSwitch ? directMode === 'total' : showBillingTotalTokens;
  const effectiveShowInput = allowModeSwitch ? directMode === 'io' : showInputTokens;
  const effectiveShowOutput = allowModeSwitch ? directMode === 'io' : showOutputTokens;

  const handleDirectModeChange = useCallback(
    (mode: DirectTokenQuotaMode) => {
      setDirectMode(mode);
      if (mode === 'total') {
        form.setFieldsValue({
          [paths.inputTokens]: undefined,
          [paths.outputTokens]: undefined,
        });
        return;
      }
      form.setFieldsValue({
        [paths.billingTotalTokens]: undefined,
      });
    },
    [form, paths],
  );

  if (!effectiveShowTotal && !effectiveShowInput && !effectiveShowOutput) {
    return null;
  }

  const ioColSpan = effectiveShowInput && effectiveShowOutput ? 12 : 24;

  return (
    <>
      {allowModeSwitch ? (
        <Form.Item label={t('data-center:model.subscription.token.quota.mode')} required={false}>
          <Radio.Group value={directMode} disabled={isPreview} onChange={(value) => handleDirectModeChange(value as DirectTokenQuotaMode)}>
            <Radio value="total">{t('data-center:model.subscription.token.quota.mode.total')}</Radio>
            <Radio value="io">{t('data-center:model.subscription.token.quota.mode.io')}</Radio>
          </Radio.Group>
        </Form.Item>
      ) : null}
      {effectiveShowTotal ? (
        <QuotaInput
          field={paths.billingTotalTokens}
          label={t('data-center:model.subscription.token.total.label')}
          rules={requiredRules}
          required={required}
          disabled={billingTotalTokensReadOnly}
          isPreview={isPreview}
          unitLabel={unitLabel}
        />
      ) : null}
      {effectiveShowInput || effectiveShowOutput ? (
        <Grid.Row gutter={16}>
          {effectiveShowInput ? (
            <Grid.Col span={ioColSpan}>
              <QuotaInput
                field={paths.inputTokens}
                label={t('data-center:model.subscription.token.input')}
                rules={requiredRules}
                required={required}
                disabled={inputTokensReadOnly}
                isPreview={isPreview}
                unitLabel={unitLabel}
              />
            </Grid.Col>
          ) : null}
          {effectiveShowOutput ? (
            <Grid.Col span={ioColSpan}>
              <QuotaInput
                field={paths.outputTokens}
                label={t('data-center:model.subscription.token.output')}
                rules={requiredRules}
                required={required}
                disabled={outputTokensReadOnly}
                isPreview={isPreview}
                unitLabel={unitLabel}
              />
            </Grid.Col>
          ) : null}
        </Grid.Row>
      ) : null}
    </>
  );
}

/** 模型订阅：模型供应商策略选择（非计价）或计价厂商设备弹窗选择（SKU / 下单） */
export function ModelSubscriptionProviderBlock({ form, paths: rawPaths, options, ...control }: ModelSubscriptionProviderBlockProps) {
  const { t } = useTranslation();
  const paths = rawPaths as ModelSubscriptionProviderPaths;
  const billModelId = options?.billModelId;
  const deploymentTypeId = options?.deploymentTypeId;
  const resourceCode = options?.resourceCode ?? MS_PROVIDER_TOKEN_RESOURCE_CODE;
  const isPricingMode = Boolean(billModelId);

  const billingTotalWatch = Form.useWatch(paths.billingTotalTokens, form);
  const inputTokensWatch = Form.useWatch(paths.inputTokens, form);
  const outputTokensWatch = Form.useWatch(paths.outputTokens, form);
  const useSubResourcePricingWatch = Form.useWatch(paths.useSubResourcePricing, form);

  const strategyReadOnly = useMemo(
    () => options?.strategyReadOnly || isFieldLocked(control, MS_PROVIDER_FIELD_KEYS.strategy),
    [control, options?.strategyReadOnly],
  );
  const billingTotalTokensReadOnly = useMemo(
    () => options?.billingTotalTokensReadOnly || isFieldLocked(control, MS_PROVIDER_FIELD_KEYS.billingTotalTokens),
    [control, options?.billingTotalTokensReadOnly],
  );
  const inputTokensReadOnly = useMemo(
    () => options?.inputTokensReadOnly || isFieldLocked(control, MS_PROVIDER_FIELD_KEYS.inputTokens),
    [control, options?.inputTokensReadOnly],
  );
  const outputTokensReadOnly = useMemo(
    () => options?.outputTokensReadOnly || isFieldLocked(control, MS_PROVIDER_FIELD_KEYS.outputTokens),
    [control, options?.outputTokensReadOnly],
  );

  const isPreview = control.blockMode === 'preview';
  const pricingDisabled = strategyReadOnly || isPreview || isFieldLocked(control, MS_PROVIDER_FIELD_KEYS.strategy);

  const defaultRules = useMemo(
    () => [
      {
        required: true,
        message: t('common:required.field'),
      },
    ],
    [t],
  );

  const showStrategy = isFieldVisible(MS_PROVIDER_FIELD_KEYS.strategy, control);
  const fieldShowTotal = isFieldVisible(MS_PROVIDER_FIELD_KEYS.billingTotalTokens, control);
  const fieldShowInput = isFieldVisible(MS_PROVIDER_FIELD_KEYS.inputTokens, control);
  const fieldShowOutput = isFieldVisible(MS_PROVIDER_FIELD_KEYS.outputTokens, control);

  const inferredUseSub = useMemo(
    () =>
      deriveMsUseSubResourcePricing({
        useSubResourcePricing: useSubResourcePricingWatch,
        billingTotalTokens: billingTotalWatch,
        inputTokens: inputTokensWatch,
        outputTokens: outputTokensWatch,
      }) ?? false,
    [billingTotalWatch, inputTokensWatch, outputTokensWatch, useSubResourcePricingWatch],
  );

  // 计价：子资源计费 → input/output；否则 → billingTotalTokens。直建：二选一由组件内切换。
  const showBillingTotalTokens = isPricingMode ? fieldShowTotal && !inferredUseSub : fieldShowTotal;
  const showInputTokens = isPricingMode ? fieldShowInput && inferredUseSub : fieldShowInput;
  const showOutputTokens = isPricingMode ? fieldShowOutput && inferredUseSub : fieldShowOutput;
  const showTokenQuotas = isPricingMode
    ? showBillingTotalTokens || showInputTokens || showOutputTokens
    : fieldShowTotal || fieldShowInput || fieldShowOutput;
  // SKU / 下单后付费：配额不展示，仍用 hidden 注册以便回显与组装收集
  const hideTokenQuotaInputs = isPricingMode && options?.paymentTiming === 'POST_PAID';

  if (!showStrategy && !showTokenQuotas && !hideTokenQuotaInputs) {
    return null;
  }

  const tokenQuotaFields = hideTokenQuotaInputs ? (
    <>
      <Form.Item field={paths.billingTotalTokens} hidden>
        <Input />
      </Form.Item>
      <Form.Item field={paths.inputTokens} hidden>
        <Input />
      </Form.Item>
      <Form.Item field={paths.outputTokens} hidden>
        <Input />
      </Form.Item>
    </>
  ) : showTokenQuotas ? (
    <TokenQuotaFields
      form={form}
      paths={paths}
      showBillingTotalTokens={showBillingTotalTokens}
      showInputTokens={showInputTokens}
      showOutputTokens={showOutputTokens}
      billingTotalTokensReadOnly={billingTotalTokensReadOnly}
      inputTokensReadOnly={inputTokensReadOnly}
      outputTokensReadOnly={outputTokensReadOnly}
      isPreview={isPreview}
      required={options?.tokenQuotaRequired ?? (isPricingMode && options?.paymentTiming !== 'POST_PAID')}
      allowModeSwitch={!isPricingMode && fieldShowTotal && (fieldShowInput || fieldShowOutput)}
      showUnit={isPricingMode}
    />
  ) : null;

  if (!showStrategy) {
    return tokenQuotaFields;
  }

  if (billModelId) {
    return (
      <>
        {/* 注册子资源计价态，避免编辑/下单回显时无法区分 Token 总量与输入/输出 */}
        <Form.Item field={paths.useSubResourcePricing} triggerPropName="checked" hidden>
          <Checkbox />
        </Form.Item>
        <BindingProviderStep form={form} initData={options?.initData} readOnly={strategyReadOnly} hidden />
        <MsPricingProviderSelector
          form={form}
          paths={paths}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          resourceCode={resourceCode}
          disabled={pricingDisabled}
        />
        {tokenQuotaFields}
      </>
    );
  }

  return (
    <>
      <BindingProviderStep form={form} defaultRules={defaultRules} initData={options?.initData} readOnly={strategyReadOnly} />
      {tokenQuotaFields}
    </>
  );
}
